import { combineReducers } from 'redux'

import { type TimeWindow } from '@dhl/inputs'
import { uniqueBy } from '@dhl/utils'
import { dataLayerPublish } from '@dhl/datalayer'

import { CAPABILITY_KEY, CARRIER, MINIMAL_PICKUP_WINDOW, PARCEL_TYPE, STEP_NAME } from '~/basket/model/constants'
// eslint-disable-next-line import-x/no-cycle -- won't fix, PWA is EOL
import {
  type CapabilitiesState,
  reducers as capabilitiesReducer,
  selectors as capabilitiesSelectors,
} from './capabilities/reducers'
// eslint-disable-next-line import-x/no-cycle -- won't fix, PWA is EOL
import {
  type Captchas,
  type Config,
  type Customs,
  type Package,
  type Payment,
  type PickupRange,
  type Promocode,
  PromocodeStatus,
  type Promocodes,
  type Receiver,
  type SessionState,
  type ShipmentState,
  type Shipper,
  reducers as sessionReducer,
  selectors as sessionSelectors,
} from './session/reducers'
import { type Price, cheapestBy, multiply, monoid as priceMonoid } from './utils/price'
import { isDhlParcel, isExpress, isExpressEconomy, isExpressOrExpressEconomy } from './utils/carrier'

import { concat } from '~/util/helpers/monoid'
import { liftSelectors } from '~/util/toGlobalSelectors'
import { sortBy } from '~/util/helpers/sortBy'
import { isFeatureEnabled } from '~/config'

/** *****************
 * Reducers
 ***************** */

export type BasketState = {
  session: SessionState
  capabilities: CapabilitiesState
}

export const basketReducer = combineReducers({
  session: sessionReducer,
  capabilities: capabilitiesReducer,
})

/** *****************
 * Selectors
 ***************** */

const isLoadingBasket = (state: BasketState): boolean => capabilitiesSelectors.isLoadingCapabilities(state.capabilities)

const getBasketError = (state: BasketState): Error | null | undefined =>
  capabilitiesSelectors.getCapabilitiesError(state.capabilities)

const getPromoCodes = (state: BasketState): Promocodes => state.session.promoCodes

const getBasketWithCapabilities = (state: BasketState) => state

export type SelectableDestinationCountry = {
  code: string
  isPopular: boolean
  ordering?: number
}

export type SelectableCarrier = {
  key: string
  transitDays: number | null
  price: Price
}

export type SelectableParcelType = {
  key: string
  carrier: string
  parcelKind:
    | 'envelope'
    | 'documents'
    | 'letterbox'
    | 'xxsmall'
    | 'xsmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge'
    | '1kg'
    | '2kg'
    | '5kg'
    | '10kg'
    | '20kg'
  minWeightKg: number
  maxWeightKg: number
  price: Price
  dimensions: {
    maxLengthCm: number
    maxWidthCm: number
    maxHeightCm: number
    maxSumCm: number | null
  }
}

export type SelectableDeliveryOption = {
  excludes?: string[] | null
  implies?: string[] | null
  key: string
  price: Price
}

export type SelectableServiceOption = {
  excludes?: string[] | null
  implies?: string[] | null
  key: string
  price: Price
}

export type SelectableHandOffOption = {
  key: string
  price: Price
}

export type Step = {
  name: string
  isDone: boolean
}

export type PickupRangeBound = {
  min: number
  max: number
}

export type Delivery = {
  expectedDeliveryDate: string | null
  days: number
}

export type Weights = {
  min: number
  max: number
}

export type BasketShipment = {
  id: number
  price: Price | null
  promocode: Promocode | null
  package: Package
  receiver: Receiver
  shipper: Shipper
  customs: Customs | null
}

export type Basket = {
  config: Config
  originCountry: string
  selectedShipmentId: number
  shipments: BasketShipment[]

  // Package page
  selectableDestinationCountries: SelectableDestinationCountry[]
  selectableCarriers: SelectableCarrier[]
  selectableParcelTypes: SelectableParcelType[]
  package: Package
  productKey: string | undefined

  // Receiver page
  selectableDeliveryOptions: SelectableDeliveryOption[]
  selectableServiceOptions: SelectableServiceOption[]
  isPhoneNumberReceiverRequired: boolean
  isParcelshopRequired: boolean
  isPersonalNoteVisible: boolean
  isReferenceVisible: boolean
  isInsuranceValueVisible: boolean
  isCustomsApplicable: boolean
  receiver: Receiver

  // Shipper page
  isPhoneNumberShipperRequired: boolean
  isEoriRequired: boolean
  shipper: Shipper

  // Customs page
  customs: Customs | null

  // Payment page
  selectableHandOffOptions: SelectableHandOffOption[]
  isPickupMomentRequired: boolean // Express
  selectablePickupDates: string[] // Express
  pickupRangeBounds: PickupRangeBound | null // Express
  pickupRangeMinimalWindow: number // Express
  isTimeWindowRequired: boolean // Parcel
  timeWindows: TimeWindow[]
  selectablePaymentOptions: SelectableServiceOption[]
  orderSubmitFailed: boolean
  payment: Payment

  price: Price | null
  delivery: Delivery | null
  weights: Weights | null
  totalPrice: Price
  isSubmitting: boolean
  promoCodes: Promocodes
  captchas: Captchas

  steps: Step[]
  currentStep: Step
}

const getBasket = (state: BasketState): Basket | null => {
  const determineCarrierKey = (firstShipment: ShipmentState): string => {
    if (
      firstShipment.package.carrierKey &&
      capabilitiesSelectors.doesCarrierShipToCountry(state.capabilities)(firstShipment.id)(
        firstShipment.package.carrierKey,
        firstShipment.package.destinationCountry
      )
    ) {
      return firstShipment.package.carrierKey
    }

    if (
      capabilitiesSelectors.doesCarrierShipToCountry(state.capabilities)(firstShipment.id)(
        CARRIER.PARCEL,
        firstShipment.package.destinationCountry
      )
    ) {
      return CARRIER.PARCEL
    }

    if (
      capabilitiesSelectors.doesCarrierShipToCountry(state.capabilities)(firstShipment.id)(
        CARRIER.EXPRESS_ECONOMY,
        firstShipment.package.destinationCountry
      )
    ) {
      return CARRIER.EXPRESS_ECONOMY
    }

    if (
      capabilitiesSelectors.doesCarrierShipToCountry(state.capabilities)(firstShipment.id)(
        CARRIER.EXPRESS,
        firstShipment.package.destinationCountry
      )
    ) {
      return CARRIER.EXPRESS
    }

    throw new Error('No carrier available')
  }

  const determineProductKey = (selectedCarrierKey: string): string | undefined => {
    const currentShipmentId = state.session.selectedShipmentId || 0

    // short circuit for express carriers, since they always return the same product key
    switch (selectedCarrierKey) {
      case CARRIER.EXPRESS:
        return 'EXPRESS'

      case CARRIER.EXPRESS_ECONOMY:
        return 'EXPRESS-ECONOMY'

      default:
        return capabilitiesSelectors.getProductKey(state.capabilities)(currentShipmentId)(selectedCarrierKey)
    }
  }

  const determineParcelTypeKey = (shipment: ShipmentState, carrierKey: string): string | null => {
    if (
      shipment.package.parcelTypeKey &&
      capabilitiesSelectors.isParcelTypeSelectable(state.capabilities)(shipment.id)(
        carrierKey,
        shipment.package.parcelTypeKey
      )
    ) {
      return shipment.package.parcelTypeKey
    }

    if (!state.session.config.isDefaultParcelTypeKeyEnabled) {
      return null
    }

    const parcelTypes = capabilitiesSelectors.getParcelTypesByCarrier(state.capabilities)(shipment.id)(carrierKey)
    if (parcelTypes.length > 0) {
      return parcelTypes[0].key
    }

    return null
  }

  const determineSelectableDestinationCountries = (
    shipment: ShipmentState,
    carrierKeyOfFirstShipment: string
  ): SelectableDestinationCountry[] => {
    const isFirstShipment = shipment.id === state.session.shipments[0].id
    const parcelDestinations =
      isFirstShipment || isDhlParcel(carrierKeyOfFirstShipment)
        ? capabilitiesSelectors.getDestinationCountriesByCarrier(state.capabilities)(shipment.id)(
            CARRIER.PARCEL,
            state.session.originCountry
          )
        : []

    const expressEconomyDestinations =
      isFirstShipment || isExpressOrExpressEconomy(carrierKeyOfFirstShipment)
        ? capabilitiesSelectors.getDestinationCountriesByCarrier(state.capabilities)(shipment.id)(
            CARRIER.EXPRESS_ECONOMY,
            state.session.originCountry
          )
        : []

    const expressDestinations =
      isFirstShipment || isExpressOrExpressEconomy(carrierKeyOfFirstShipment)
        ? capabilitiesSelectors.getDestinationCountriesByCarrier(state.capabilities)(shipment.id)(
            CARRIER.EXPRESS,
            state.session.originCountry
          )
        : []

    return uniqueBy(
      [...new Set([...parcelDestinations, ...expressEconomyDestinations, ...expressDestinations])],
      ({ code }) => code
    )
  }

  const determineSelectableCarriers = (
    shipment: ShipmentState,
    selectedDestinationCountry: string,
    carrierKeyOfFirstShipment: string
  ): SelectableCarrier[] => {
    const isFirstShipment = shipment.id === state.session.shipments[0].id
    const isExpressEconomyEnabled = isFeatureEnabled('expressEconomy')

    const doesCarrierShipToSelectedDestination = (carrierKey: string): boolean =>
      capabilitiesSelectors.doesCarrierShipToCountry(state.capabilities)(shipment.id)(
        carrierKey,
        selectedDestinationCountry
      )

    // The sequence of the carriers is important. The first carrier is shown as the normal/slow option. And the second
    // carrier is shown as the default option. The CARRIER.EXPRESS_ECONOMY and CARRIER.PARCEL should be configured in
    // the capability service to be mutually exclusive. If both are returned for a specific Country/Parcel combination,
    // the CARRIER.EXPRESS_ECONOMY should be ignored.
    const carriers = [
      {
        key: CARRIER.PARCEL,
        isSelectable:
          doesCarrierShipToSelectedDestination(CARRIER.PARCEL) &&
          (isFirstShipment || isDhlParcel(carrierKeyOfFirstShipment)),
      },
      {
        key: CARRIER.EXPRESS_ECONOMY,
        isSelectable:
          doesCarrierShipToSelectedDestination(CARRIER.EXPRESS_ECONOMY) &&
          (isFirstShipment || isExpressOrExpressEconomy(carrierKeyOfFirstShipment)) &&
          isExpressEconomyEnabled,
      },
      {
        key: CARRIER.EXPRESS,
        isSelectable:
          doesCarrierShipToSelectedDestination(CARRIER.EXPRESS) &&
          (isFirstShipment ||
            isExpress(carrierKeyOfFirstShipment) ||
            (isExpressEconomy(carrierKeyOfFirstShipment) && isExpressEconomyEnabled)),
      },
    ]

    const selectableCarriers = carriers.filter(({ isSelectable }) => isSelectable)

    return selectableCarriers.map(({ key }) => {
      const price = capabilitiesSelectors.getMinimumPriceCarrier(state.capabilities)(shipment.id)(key)
      const transitDays = capabilitiesSelectors.getTransitDaysByCarrierAndShipment(state.capabilities)(shipment.id)(key)

      return {
        key,
        transitDays,
        price,
      }
    })
  }

  const determineSelectableParcelTypes = (
    shipment: ShipmentState,
    selectedCarrierKey: string,
    originCountry: string
  ): SelectableParcelType[] =>
    sortBy(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      capabilitiesSelectors.getParcelTypesByCarrier(state.capabilities)(shipment.id)(selectedCarrierKey) as any[],
      (parcelType) => (parcelType.dimensions.maxSumCm ? parcelType.dimensions.maxSumCm : parcelType.maxWeightKg)
    ).map((parcelType) => {
      // eslint-disable-next-line consistent-return
      const getParcelKind = () => {
        const dhlParcel = isDhlParcel(selectedCarrierKey)
        const express = isExpress(selectedCarrierKey)
        const expressEconomy = isExpressEconomy(selectedCarrierKey)

        // eslint-disable-next-line default-case
        switch (parcelType.key) {
          case PARCEL_TYPE.ENVELOPE:
            return 'envelope'
          case PARCEL_TYPE.XXSMALL:
            return 'xxsmall'
          case PARCEL_TYPE.XSMALL:
            if (originCountry === 'NL') {
              return isExpressOrExpressEconomy(selectedCarrierKey) ? 'documents' : 'letterbox'
            }
            return 'xsmall'
          case PARCEL_TYPE.SMALL:
            if (express) {
              return '1kg'
            }
            if (expressEconomy) {
              return '2kg'
            }
            return 'small'

          case PARCEL_TYPE.MEDIUM:
            if (dhlParcel) {
              return 'medium'
            }
            if (expressEconomy) {
              return '5kg'
            }
            return '2kg'

          case PARCEL_TYPE.LARGE:
            if (dhlParcel) {
              return 'large'
            }
            if (expressEconomy) {
              return '10kg'
            }
            return '5kg'

          case PARCEL_TYPE.XLARGE:
            if (dhlParcel) {
              return 'xlarge'
            }
            if (expressEconomy) {
              return '20kg'
            }
            return '10kg'

          case PARCEL_TYPE.XXLARGE:
            if (dhlParcel) {
              return 'xxlarge'
            }
            return '20kg'
        }

        dataLayerPublish('EVENT', {
          id: 'determineSelectableParcelTypes',
          message: `Unknown parcel type: ${parcelType.key}`,
        })
      }

      return {
        ...parcelType,
        carrier: selectedCarrierKey,
        parcelKind: getParcelKind(),
      }
    })

  const isCustomsApplicable = (shipmentId: number, parcelTypeKey: string | null) =>
    capabilitiesSelectors.isCustomsDestinationCountry(state.capabilities)(shipmentId) &&
    parcelTypeKey !== PARCEL_TYPE.XSMALL

  const determineSteps = (shipment: ShipmentState, parcelTypeKey: string | null): Step[] => {
    const steps = [
      {
        name: STEP_NAME.PACKAGE,
        isApplicable: true,
      },
      {
        name: STEP_NAME.RECEIVER,
        isApplicable: true,
      },
      {
        name: STEP_NAME.SHIPPER,
        isApplicable: true,
      },
      {
        name: STEP_NAME.CUSTOMS,
        isApplicable: isCustomsApplicable(shipment.id, parcelTypeKey),
      },
      {
        name: STEP_NAME.OVERVIEW,
        isApplicable: true,
      },
      {
        name: STEP_NAME.PARCELSHOP_PAYMENT,
        isApplicable: state.session.config.isParcelshopPaymentEnabled,
      },
      {
        name: STEP_NAME.LABEL,
        isApplicable: true,
      },
    ]

    const applicableSteps = steps.filter(({ isApplicable }) => isApplicable)

    return applicableSteps.map(({ name }) => ({
      name,
      isDone: shipment.confirmedSteps.includes(name),
    }))
  }

  const determineCurrentStep = (steps: Step[]): Step => {
    const currentStep = steps.find((step) => !step.isDone)
    if (currentStep) {
      return currentStep
    }

    throw new Error('Unable to determine current step')
  }

  const determinePriceAndDiscount = (
    shipment: ShipmentState,
    basePrice: Price | null | undefined,
    selectedCarrierKey: string,
    selectableDeliveryOptions: SelectableDeliveryOption[],
    deliveryOptionKey: string | null,
    selectableServiceOptions: SelectableServiceOption[],
    serviceOptionKeys: string[]
  ): { price: Price | null; promocode: Promocode | null } => {
    if (!basePrice) {
      return {
        price: null,
        promocode: null,
      }
    }
    const isDeliveryOptionSelected = (key: string) => key === deliveryOptionKey
    const isSeviceOptionSelected = (key: string) => serviceOptionKeys.includes(key)

    const deliveryPrices = selectableDeliveryOptions
      .filter((option) => isDeliveryOptionSelected(option.key))
      .map(({ price }) => price)
    const servicePrices = selectableServiceOptions
      .filter((option) => isSeviceOptionSelected(option.key))
      .map(({ price }) => price)

    const isPromocodeApplicable = (promocode: Promocode) => {
      if (!promocode.discount || promocode.status !== PromocodeStatus.Valid) {
        return false
      }

      const { destinationCountries } = promocode.discount
      if (destinationCountries.length !== 0 && !destinationCountries.includes(shipment.package.destinationCountry)) {
        return false
      }

      const { carrier } = promocode.discount
      if (carrier !== null && carrier !== selectedCarrierKey) {
        return false
      }
      return true
    }

    if (shipment.promocode && shipment.promocode.discount && isPromocodeApplicable(shipment.promocode)) {
      return {
        price: concat(priceMonoid)([
          multiply(basePrice, 1 - shipment.promocode.discount.percentage),
          ...deliveryPrices,
          ...servicePrices,
        ]),
        promocode: shipment.promocode,
      }
    }

    return {
      price: concat(priceMonoid)([basePrice, ...deliveryPrices, ...servicePrices]),
      promocode: null,
    }
  }

  const determineDelivery = (
    shipment: ShipmentState,
    selectedCarrierKey: string,
    pickupDate: string | null
  ): Delivery | null => {
    const expectedDeliveryDate = capabilitiesSelectors.getExpectedDeliveryDateByCarrierAndShipment(state.capabilities)(
      shipment.id
    )(selectedCarrierKey, pickupDate)
    const transitDays = capabilitiesSelectors.getTransitDaysByCarrierAndShipment(state.capabilities)(shipment.id)(
      selectedCarrierKey,
      pickupDate
    )

    if (!expectedDeliveryDate || !transitDays) {
      return null
    }

    return {
      expectedDeliveryDate,
      days: transitDays,
    }
  }

  const determineWeights = (
    shipment: ShipmentState,
    selectedCarrierKey: string,
    parcelTypeKey: string | undefined | null
  ): Weights | null => {
    if (!parcelTypeKey) {
      return null
    }

    const result = capabilitiesSelectors.getParcelTypeByCarrierAndParcelTypeKey(state.capabilities)(shipment.id)(
      selectedCarrierKey,
      parcelTypeKey
    )

    return {
      min: result.minWeightKg,
      max: result.maxWeightKg,
    }
  }

  const determineSelectableServiceOptions = (
    shipment: ShipmentState,
    carrierKey: string,
    deliveryOptionKey: string | null
  ) => {
    const selectableServiceOptions = capabilitiesSelectors
      .getServiceOptionsByCarrier(state.capabilities)(shipment.id)(
        carrierKey,
        deliveryOptionKey ? [deliveryOptionKey] : []
      )
      .filter((option) => option.key !== CAPABILITY_KEY.PRINTLESS)

    const updatePrice = (option: SelectableServiceOption) => {
      switch (option.key) {
        case CAPABILITY_KEY.X_II:
          return {
            key: option.key,
            price: shipment.receiver.insuranceValue
              ? multiply(option.price, Math.max(1, shipment.receiver.insuranceValue / 1000))
              : option.price,
          }
        default:
          return option
      }
    }

    return selectableServiceOptions.map(updatePrice)
  }

  const determineDeliveryOptionKey = (
    shipment: ShipmentState,
    selectableDeliveryOptions: SelectableDeliveryOption[]
  ): string | null => {
    if (
      shipment.receiver.deliveryOptionKey &&
      selectableDeliveryOptions.some(({ key }) => key === shipment.receiver.deliveryOptionKey)
    ) {
      return shipment.receiver.deliveryOptionKey
    }

    if (selectableDeliveryOptions.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return selectCheapestOption(selectableDeliveryOptions).key
    }

    return null
  }

  const determineServiceOptionKeys = (
    shipment: ShipmentState,
    selectableServiceOptions: SelectableServiceOption[]
  ): string[] =>
    shipment.receiver.serviceOptionKeys.filter((serviceOptionKey) =>
      selectableServiceOptions.some(({ key }) => serviceOptionKey === key)
    )

  const determineHandOffKey = (
    session: SessionState,
    selectableHandOffOptions: SelectableHandOffOption[]
  ): string | null => {
    if (
      session.payment.handOffKey &&
      selectableHandOffOptions.find((handOffOption) => handOffOption.key === session.payment.handOffKey)
    ) {
      return session.payment.handOffKey
    }

    if (selectableHandOffOptions.length === 1) {
      return selectableHandOffOptions[0].key
    }

    return null
  }

  const determineSelectablePickupDates = (selectedCarrierKey: string) => {
    if (!isExpressOrExpressEconomy(selectedCarrierKey)) {
      return []
    }

    return capabilitiesSelectors.getPickupDatesByCarrier(state.capabilities, selectedCarrierKey)
  }

  const determinePickupDate = (session: SessionState, selectablePickupDays: string[]): string | null => {
    if (selectablePickupDays.length === 0) {
      return null
    }

    if (!session.payment.pickupDate) {
      return selectablePickupDays[0]
    }

    return session.payment.pickupDate
  }

  const determinePickupRangeBound = (
    selectedCarrierKey: string,
    pickupDate: string | null
  ): PickupRangeBound | null => {
    if (!isExpressOrExpressEconomy(selectedCarrierKey)) {
      return null
    }

    return capabilitiesSelectors.getPickupRangeBoundByCarrier(state.capabilities, selectedCarrierKey, pickupDate)
  }

  const determinePickupRange = (
    session: SessionState,
    pickupRangeBound: PickupRangeBound | null
  ): PickupRange | null => {
    if (!pickupRangeBound) {
      return null
    }

    if (!session.payment.pickupRange) {
      return {
        min: pickupRangeBound.min,
        max: pickupRangeBound.min + MINIMAL_PICKUP_WINDOW,
      }
    }

    return session.payment.pickupRange
  }

  const determineBasePrice = (
    shipment: ShipmentState,
    carrierKey: string,
    parcelTypeKey: string | null
  ): Price | null | undefined => {
    if (!parcelTypeKey) {
      return null
    }

    return capabilitiesSelectors.getParcelTypeByCarrierAndParcelTypeKey(state.capabilities)(shipment.id)(
      carrierKey,
      parcelTypeKey
    ).price
  }

  type State = BasketShipment & {
    package: Package & {
      carrierKey: string
    }
    selectableDeliveryOptions: SelectableDeliveryOption[]
    selectableServiceOptions: SelectableServiceOption[]
  }

  const determineShipments = (selectableCarriers: SelectableCarrier[]): State[] => {
    const shipmentsWithBasePrice = state.session.shipments.map((shipment) => {
      const carrierKey = selectableCarriers.some((c) => c.key === shipment.package.carrierKey)
        ? shipment.package.carrierKey
        : selectableCarriers[0].key
      const parcelTypeKey = determineParcelTypeKey(shipment, carrierKey)
      const basePrice = determineBasePrice(shipment, carrierKey, parcelTypeKey)

      return {
        ...shipment,
        package: {
          ...shipment.package,
          carrierKey,
          parcelTypeKey,
        },
        basePrice,
      }
    })

    const shipmentsSortedByBasePrice = sortBy(shipmentsWithBasePrice, (shipment) =>
      shipment.basePrice ? -1 * shipment.basePrice.withTax : 0
    )

    const shipmentsWithOptions = shipmentsSortedByBasePrice.reduce<State[]>((acc, shipment) => {
      const { carrierKey } = shipment.package
      const { parcelTypeKey } = shipment.package

      // Delivery options
      const selectableDeliveryOptions = capabilitiesSelectors.getDeliveryOptionsByCarrier(state.capabilities)(
        shipment.id
      )(carrierKey)

      const deliveryOptionKey = determineDeliveryOptionKey(shipment, selectableDeliveryOptions)

      // Service options
      const allServiceOptions = determineSelectableServiceOptions(shipment, carrierKey, deliveryOptionKey)
      const selectableServiceOptions = allServiceOptions.filter(
        (option) => option.key !== CAPABILITY_KEY.EXPRESS_PAPERLESS_TRADE
      )

      const serviceOptionKeys = determineServiceOptionKeys(shipment, selectableServiceOptions)

      const { price, promocode } = determinePriceAndDiscount(
        shipment,
        shipment.basePrice,
        carrierKey,
        selectableDeliveryOptions,
        deliveryOptionKey,
        allServiceOptions,
        serviceOptionKeys
      )

      return [
        ...acc,
        {
          id: shipment.id,
          price,
          promocode,
          package: {
            ...shipment.package,
            carrierKey,
            parcelTypeKey,
          },
          receiver: {
            ...shipment.receiver,
            deliveryOptionKey,
            serviceOptionKeys,
          },
          shipper: shipment.shipper,
          customs: isCustomsApplicable(shipment.id, parcelTypeKey)
            ? {
                ...shipment.customs,
                isExpressPaperlessTrade: isExpressOrExpressEconomy(carrierKey)
                  ? allServiceOptions.some((option) => option.key === CAPABILITY_KEY.EXPRESS_PAPERLESS_TRADE)
                  : undefined,
              }
            : null,

          selectableDeliveryOptions,
          selectableServiceOptions,
        },
      ]
    }, [])

    return sortBy(shipmentsWithOptions, (shipment) => shipment.id)
  }

  const determineSelectablePaymentOptions = (handOffKey: string | null, selectedCarrierKey: string) => {
    const defaultPaymentOption: SelectableServiceOption = { key: 'PRINT', price: { withTax: 0, withoutTax: 0 } }
    const printlessOptions = state.session.shipments.map((shipment) =>
      capabilitiesSelectors
        .getServiceOptionsByCarrier(state.capabilities)(shipment.id)(selectedCarrierKey, handOffKey ? [handOffKey] : [])
        .filter((o) => o.excludes?.every((x) => !shipment.receiver.serviceOptionKeys.includes(x)))
        .find((o) => o.key === CAPABILITY_KEY.PRINTLESS)
    )

    return printlessOptions.length > 0 && printlessOptions.every((o) => !!o)
      ? [printlessOptions[0], defaultPaymentOption]
      : []
  }

  const determinePaymentOptionKey = (selectableOptions: SelectableServiceOption[]) => {
    if (selectableOptions.some(({ key }) => key === state.session.payment.paymentOptionKey)) {
      return state.session.payment.paymentOptionKey
    }
    return ''
  }

  const determineTotalPrice = (
    shipments: BasketShipment[],
    selectableHandOffOptions: SelectableHandOffOption[],
    handOffKey: string | null,
    selectablePaymentOptions: SelectableServiceOption[],
    selectedPaymentOption: string
  ): Price => {
    const handOffPrices = selectableHandOffOptions.filter(({ key }) => key === handOffKey).map(({ price }) => price)

    const paymentOptionPrices = selectablePaymentOptions
      .filter(({ key }) => key === selectedPaymentOption)
      .flatMap(({ price }) => shipments.map(() => price))

    return shipments
      .filter(({ price }) => price)
      .map(({ price }) => price!)
      .concat(handOffPrices)
      .concat(paymentOptionPrices)
      .reduce(priceMonoid.append, priceMonoid.zero)
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const shipmentState = basketSelectors.getSelectedShipment(state)
  if (!shipmentState) {
    return null
  }
  if (!capabilitiesSelectors.hasCapabilities(state.capabilities)(shipmentState.id)) {
    return null
  }

  const carrierKeyOfFirstShipment = determineCarrierKey(state.session.shipments[0])

  const selectedDestinationCountry = shipmentState.package.destinationCountry

  const selectableCarriers = determineSelectableCarriers(
    shipmentState,
    selectedDestinationCountry,
    carrierKeyOfFirstShipment
  )

  const shipments = determineShipments(selectableCarriers)

  const selectedShipment = shipments.find(({ id }) => id === state.session.selectedShipmentId)
  if (!selectedShipment) {
    throw new Error('Selected shipment ID does not exists')
  }

  const {
    package: p,
    receiver,
    shipper,
    customs,
    price,
    selectableDeliveryOptions,
    selectableServiceOptions,
  } = selectedShipment

  const selectedCarrierKey = selectableCarriers.some((c) => c.key === p.carrierKey)
    ? p.carrierKey
    : selectableCarriers[0].key

  const { trackerCode } = p
  const { parcelTypeKey } = p
  const steps = determineSteps(shipmentState, parcelTypeKey)

  // Hand off options
  const selectableHandOffOptions = capabilitiesSelectors.getHandOffOptionsByCarrier(
    state.capabilities,
    selectedCarrierKey,
    state.session.shipments
  )
  const handOffKey = determineHandOffKey(state.session, selectableHandOffOptions)

  // Pickup date and time for parcel
  const timeWindows = capabilitiesSelectors.getTimeWindows(state.capabilities, selectedCarrierKey)

  // Pickup date and range for express
  const pickupRangeBounds = determinePickupRangeBound(selectedCarrierKey, state.session.payment.pickupDate)
  const pickupRange = determinePickupRange(state.session, pickupRangeBounds)

  const selectablePickupDates = determineSelectablePickupDates(selectedCarrierKey)
  const pickupDate = determinePickupDate(state.session, selectablePickupDates)

  const selectablePaymentOptions = determineSelectablePaymentOptions(handOffKey, selectedCarrierKey)
  const selectedPaymentOption = determinePaymentOptionKey(selectablePaymentOptions)

  const selectableDestinationCountries = determineSelectableDestinationCountries(
    shipmentState,
    carrierKeyOfFirstShipment
  )

  const selectableParcelTypes = determineSelectableParcelTypes(
    shipmentState,
    selectedCarrierKey,
    state.session.originCountry
  )

  const basket: Basket = {
    config: state.session.config,
    originCountry: state.session.originCountry,
    selectedShipmentId: state.session.selectedShipmentId!,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    shipments: shipments.map(({ id, price, promocode, package: p, receiver, shipper, customs }) => ({
      id,
      promocode,
      price,
      package: p,
      receiver,
      shipper,
      customs,
    })),

    // Package page
    selectableDestinationCountries,
    selectableParcelTypes,
    selectableCarriers,
    package: {
      trackerCode,
      destinationCountry: selectedDestinationCountry,
      carrierKey: selectedCarrierKey,
      parcelTypeKey,
    },
    productKey: determineProductKey(selectedCarrierKey),

    // Delivery page
    selectableDeliveryOptions,
    selectableServiceOptions,
    isPhoneNumberReceiverRequired: isExpressOrExpressEconomy(selectedCarrierKey),
    isParcelshopRequired: receiver.deliveryOptionKey === CAPABILITY_KEY.PS,
    isPersonalNoteVisible: receiver.serviceOptionKeys.includes(CAPABILITY_KEY.PERS_NOTE),
    isReferenceVisible: receiver.serviceOptionKeys.includes(CAPABILITY_KEY.REFERENCE),
    isInsuranceValueVisible: receiver.serviceOptionKeys.includes(CAPABILITY_KEY.X_II),
    receiver,
    isCustomsApplicable: isCustomsApplicable(selectedShipment.id, selectedShipment.package.parcelTypeKey),
    // Shipper page
    isPhoneNumberShipperRequired:
      isExpressOrExpressEconomy(selectedCarrierKey) || ['ES', 'PT', 'SE'].indexOf(state.session.originCountry) >= 0,
    isEoriRequired: selectedShipment.package.destinationCountry === 'GB',
    shipper,

    // Customs page
    customs,

    // Payment
    selectablePaymentOptions,
    selectableHandOffOptions,
    selectablePickupDates,
    isPickupMomentRequired: handOffKey === CAPABILITY_KEY.X_PI,
    pickupRangeBounds,
    pickupRangeMinimalWindow: 3,
    isTimeWindowRequired: handOffKey === CAPABILITY_KEY.PICKUP,
    timeWindows,
    orderSubmitFailed: state.session.orderSubmitFailed,
    payment: {
      ...state.session.payment,
      handOffKey,
      pickupDate,
      pickupRange,
      paymentOptionKey: selectedPaymentOption,
    },

    // Information about selected shipment
    price,
    delivery: determineDelivery(shipmentState, selectedCarrierKey, pickupDate),
    weights: determineWeights(shipmentState, selectedCarrierKey, parcelTypeKey),

    promoCodes: state.session.promoCodes,
    captchas: state.session.captchas,

    // Information about basket
    steps,
    currentStep: determineCurrentStep(steps),
    totalPrice: determineTotalPrice(
      shipments,
      selectableHandOffOptions,
      handOffKey,
      selectablePaymentOptions,
      selectedPaymentOption
    ),
    isSubmitting: state.session.isSubmitting,
  }

  return basket
}

const getParcelTypeKeyByWeight = (state: BasketState) => (weight: number) => {
  const basket = getBasket(state)

  if (basket === null) {
    throw new Error('Unable to determine parcel type by weight, basket is not available')
  }

  const parcelTypes = basket.selectableParcelTypes
  const selectedParcelType =
    parcelTypes.find((parcelType) => parcelType.minWeightKg <= weight && weight <= parcelType.maxWeightKg) ||
    parcelTypes[0]

  return selectedParcelType.key
}

export const basketSelectors = {
  ...liftSelectors((state: BasketState) => state.session, sessionSelectors),
  getBasket,
  isLoadingBasket,
  getBasketError,
  getParcelTypeKeyByWeight,
  getPromoCodes,
  getBasketWithCapabilities,
}

function selectCheapestOption(options: SelectableDeliveryOption[]): SelectableDeliveryOption {
  return cheapestBy(options, (option) => {
    if (!option.price) {
      throw new Error(`Option with key '${option.key}' has no price`)
    }

    return option.price
  })
}
